import React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../../components/ui/form";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import Spinner from "../../../components/ui/spinner";
import { useSaveProjectAsTemplateMutation } from "../../../data/mutations/save-project-as-template";

interface Props {
    projectId: string;
    projectName: string;
    isOpen: boolean;
    onClose: () => void;
}

const formSchema = z.object({
    name: z.string().min(1, "Template name is required"),
    description: z.string().min(1, "Template description is required"),
});

type FormType = z.infer<typeof formSchema>;

export function SaveAsTemplateDialog({ projectId, projectName, isOpen, onClose }: Props) {
    const navigate = useNavigate();
    const saveTemplateMutation = useSaveProjectAsTemplateMutation({
        onSuccess: () => {
            navigate("/templates");
        },
    });

    const form = useForm<FormType>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            name: `${projectName} Template`,
            description: "",
        },
    });

    function onSubmit(values: FormType) {
        saveTemplateMutation.mutate({
            projectId,
            name: values.name,
            description: values.description,
        });
    }

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>Save as Template</DialogTitle>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                        <FormField
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Template Name</FormLabel>
                                    <FormControl>
                                        <Input
                                            disabled={saveTemplateMutation.isPending}
                                            placeholder="e.g. Marketing Campaign Template"
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="description"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Description</FormLabel>
                                    <FormControl>
                                        <Textarea
                                            disabled={saveTemplateMutation.isPending}
                                            placeholder="Describe what this template is for and when to use it..."
                                            rows={4}
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {saveTemplateMutation.isError && (
                            <div className="rounded-md border border-destructive px-4 py-2 text-sm text-destructive">
                                Failed to save template. Please try again.
                            </div>
                        )}

                        <div className="flex flex-row justify-end gap-2 pt-4">
                            <Button
                                type="button"
                                variant="secondary"
                                disabled={saveTemplateMutation.isPending}
                                onClick={onClose}
                            >
                                Cancel
                            </Button>
                            <Button type="submit" disabled={saveTemplateMutation.isPending}>
                                {saveTemplateMutation.isPending && <Spinner className="me-2 h-4 w-4" />}
                                Save template
                            </Button>
                        </div>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}
